import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import _ from 'lodash';
import { BehaviorSubject, Observable, combineLatest, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Brand } from '../../../models/brand';
import { MeUser } from '../../../models/me-user';
import { AuthenticationService } from '../../../services/authentication.service';
import { IPayload, Payload } from '../../../services/payload/models/payload';
import { IPeriodAndSurveyTypesFormGroup } from '../components/period-and-survey-type/period-and-survey-type-form-builder';
import { FavoriteFilter } from '../models/favoriteFilter';
import { Filters, IFilters } from '../models/filters';
import { IPeriodAndSurveyTypeFilters, PeriodAndSurveyTypeFilters } from '../models/periodAndSurveyTypeFilters';
import { IProductFormGroup } from '../utils/filters-form-builder';
import { FiltersApiService } from './filters.api.service';
import { INetworkFilters, NetworkFilters } from '../models/networkFilters';
import { IProductFilters, ProductFilters } from '../models/productFilters';
import { NetworkFiltersFormBuilder } from '../components/network/network-form-builder';
import { LoaderService } from '../../../services/loader.service';
import { Network } from '../../../services/payload/models/network';
import { SelectOption } from '../components/multi-select-autocomplete/multi-select-autocomplete.component';
import { PayloadFactoryService } from "./payload.factory.service";
import {MatomoService} from "../../../services/matomo.service";

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private _selectedBrands$ = new BehaviorSubject<string[]>([]);
  brands: BehaviorSubject<Brand[]> = new BehaviorSubject(new Array<Brand>());
  toggleFilters: BehaviorSubject<boolean> = new BehaviorSubject(false);
  appliedFilters: BehaviorSubject<IPayload> = new BehaviorSubject(this.payloadFactoryService.createPayload());
  currentFilters: BehaviorSubject<IFilters> = new BehaviorSubject(new Filters());
  resetFilter: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loadFilter: BehaviorSubject<FavoriteFilter> = new BehaviorSubject(new FavoriteFilter());
  isFormValid: boolean = false;
  displayedFilters: BehaviorSubject<Map<keyof IFilters, (keyof INetworkFilters | keyof IProductFilters | keyof IPeriodAndSurveyTypeFilters)[]>> =
    new BehaviorSubject<Map<keyof IFilters, (keyof INetworkFilters | keyof IProductFilters | keyof IPeriodAndSurveyTypeFilters)[]>>(new Map([
      ['network', []],
      ['product', []],
      ['periodAndSurveyType', []],
    ]))
  networkFiltersFormBuilder!: NetworkFiltersFormBuilder;
  // networkFormBuilder$ = new BehaviorSubject<NetworkFiltersFormBuilder | null>(null);
  productForm$ = new BehaviorSubject<FormGroup<IProductFormGroup> | null>(null);
  periodAndSurveyTypeForm$ = new BehaviorSubject<FormGroup<IPeriodAndSurveyTypesFormGroup> | null>(null);
  dealersSubject = new BehaviorSubject<{ option: SelectOption[], label: string }>({ option: [], label: '' });
  outletsSubject = new BehaviorSubject<{ option: SelectOption[], label: string }>({ option: [], label: '' });
  areaSubject = new BehaviorSubject<{ option: SelectOption[], label: string }>({ option: [], label: '' });


  timePeriodAndSurveyCompletionInitialValues$ = new BehaviorSubject<PeriodAndSurveyTypeFilters | null>(null);

  selectedBrands$ = this._selectedBrands$.asObservable();

  private formsStatus: { [k in keyof IFilters]: boolean } = {
    network: false,
    product: false,
    periodAndSurveyType: false,
  };

  constructor(private loaderService: LoaderService, private auth: AuthenticationService, private apiService: FiltersApiService,
              private payloadFactoryService: PayloadFactoryService, private matomo: MatomoService) {
    this.networkFiltersFormBuilder = new NetworkFiltersFormBuilder(this.apiService);
    this.onNetworkFiltersChange();
  }

  public initFilters() {
    this.loaderService.startLoader()
    forkJoin({
      brands: this.getBrands(this.auth._userSubject.value),
      user: of(this.auth._userSubject.value)
    }).subscribe(({ brands, user }) => {
      this.initNetworkFilter(brands, user);
      this.initProductFilters();
      this.loaderService.stopLoader()
    })
  }

  clearFilters() {
    this.networkFiltersFormBuilder.updateValues(new Network());
    this.appliedFilters.next(this.payloadFactoryService.createPayload());
  }

  private initNetworkFilter(brands: Brand[], user: MeUser) {
    this.networkFiltersFormBuilder.setInitialValues(user, brands);
    this.applyInitialFilters('network', this.networkFiltersFormBuilder.form.getRawValue(), this.networkFiltersFormBuilder.form.valid);
  }

  private initProductFilters() {

  }

  setResetFilters() {
    this.resetFilter.next(true)
  }

  updateFilters<T extends keyof IFilters>(section: T, value: IFilters[T], isValid: boolean) {
    const currentFilters = this.currentFilters.value;
    currentFilters[section] = value;
    this.formsStatus[section] = isValid;
    this.currentFilters.next(currentFilters);
    this.updatefiltersStatus();
  }

  private trackEventTimeout: any = null;


  applyInitialFilters<T extends keyof Filters>(section: T, value: Filters[T], isValid: boolean) {
    this.updateFilters(section, value, isValid);
    let appliedFilters = this.applyFilters();

    // we need timeout because applyInitialFilters is called on form creation for each fields
    if (this.trackEventTimeout) {
      clearTimeout(this.trackEventTimeout);
    }

    this.trackEventTimeout = setTimeout(() => {
      this.matomo.trackEvent('Filter', 'app created', JSON.stringify(appliedFilters));
      this.trackEventTimeout = null;
    }, 1000);

  }

  toggleFilter(forcedValue?: boolean) {
    const value = forcedValue !== undefined ? forcedValue : !this.toggleFilters.value;
    this.toggleFilters.next(value);
  }

  applyFilters(favoriteFilter?: FavoriteFilter): Payload | undefined {
    this.updatefiltersStatus()
    this.isFormValid = true;
    if (this.isFormValid) {
      const value = _.cloneDeep(this.currentFilters.value);
      const payload = this.payloadFactoryService.createPayload(value);
      let nextFilter = payload;
      if (favoriteFilter && favoriteFilter.filter) {
        nextFilter = favoriteFilter.filter;
        nextFilter.question.questionCode = '***';
      }

      this.appliedFilters.next(nextFilter);
      this.toggleFilters.next(false);
      return nextFilter;
    }
    return undefined;
  }

  private getBrands(user: MeUser): Observable<Brand[]> {
    return this.apiService.getBrands().pipe(
      tap((brands) => {
        this.brands.next(brands);
      })
    );
  }

  private updatefiltersStatus() {
    const validity: boolean[] = Object.values(this.formsStatus);
    this.isFormValid = !validity.includes(false);
  }

  getFavFilters() {
    //return this.apiService.getFavoriteFilter();
  }

  getFavFilter(filterName: string): Observable<FavoriteFilter[]> {
    return this.apiService.getFavoriteFilter(filterName).pipe(
      map((response) => {
        response.forEach((res) => {
          this.hasNullValues(res);
        });
        return response;
      })
    );
  }

  private onNetworkFiltersChange() {
    this.networkFiltersFormBuilder.applyNetworkFilters.subscribe(() => {
      this.applyInitialFilters('network', this.networkFiltersFormBuilder.form.getRawValue(), this.networkFiltersFormBuilder.form.valid);
    })

    combineLatest({
      value: this.networkFiltersFormBuilder.form.valueChanges,
      status: this.networkFiltersFormBuilder.form.statusChanges,
    })
      .subscribe(({ value, status }) => {
        const valid: boolean = status == 'VALID';
        this.updateFilters('network', this.networkFiltersFormBuilder.form.getRawValue(), valid);
      });
  }

  setFavFilter(favFilter: FavoriteFilter) {
    return this.apiService.setFavoriteFilter(favFilter);
  }

  deleteFavFilter(filterName: string) {
    return this.apiService.deleteFavoriteFilter(filterName);
  }

  hasNullValues(obj: any): boolean {
    if (typeof obj === 'object' && obj !== null) {
      // Check for object and not null
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && this.hasNullValues(obj[key])) {
          obj[key] = [];
        }
      }
    }
    return obj === null || obj === undefined; // Check for null and undefined
  }

  updateDisplayedFilters(section: keyof IFilters, filters: (keyof INetworkFilters | keyof IProductFilters | keyof IPeriodAndSurveyTypeFilters)[]) {
    switch (section) {
      case 'network':
        this.networkFiltersFormBuilder.updateAvailableFilters(filters as (keyof INetworkFilters)[]);
        break;
      default:
        break;
    }
    const value = this.displayedFilters.getValue()
    value.set(section, filters)
    this.displayedFilters.next(value);
  }
}
